import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import PublicNav from "../components/PublicNav";
import {
  Check,
  ArrowRight,
  Sparkles,
  Gift,
  Zap,
  Clock,
} from "lucide-react";

export default function PricingPage() {
  const features = [
    "Unlimited AI Agents",
    "Unlimited messages",
    "Lead capture & management",
    "Appointment booking",
    "Analytics dashboard",
    "WhatsApp integration",
    "Custom knowledge base",
    "Email support"
  ];

  const faqs = [
    {
      question: "Is Botibonga really free?",
      answer: "Yes! During our early access period, all features are completely free. We want you to experience the full power of AI-powered customer support without any barriers."
    },
    {
      question: "Will there be paid plans in the future?",
      answer: "We may introduce paid plans with advanced features in the future, but we'll always have a generous free tier. Early access users will receive special benefits when we launch paid plans."
    },
    {
      question: "Are there any hidden fees?",
      answer: "No hidden fees at all. What you see is what you get - full access to all features at no cost during early access."
    },
    {
      question: "How do I get started?",
      answer: "Simply sign up with your Google account and you can create your first AI agent in under 10 minutes. No credit card required."
    }
  ];

  return (
    <>
      <SEO
        title="Pricing"
        description="Botibonga is completely free during early access. Build AI-powered WhatsApp chatbots with unlimited agents, messages, and all features at no cost."
        path="/pricing"
      />

      <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)' }}>
        <PublicNav />

        {/* Hero */}
        <section className="px-6 py-16 lg:py-24">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-6"
                 style={{
                   background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)',
                   border: '1px solid rgba(34, 197, 94, 0.3)'
                 }}>
              <Gift className="w-3.5 h-3.5" style={{ color: '#22c55e' }} />
              <span style={{ color: '#22c55e' }}>Early Access - Free</span>
            </div>

            <h1 className="text-3xl lg:text-5xl font-bold mb-6" style={{ color: '#1e293b' }}>
              Completely{" "}
              <span style={{
                background: 'linear-gradient(90deg, #22c55e 0%, #10b981 50%, #059669 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>free</span>
              {" "}during early access
            </h1>

            <p className="text-lg mb-8" style={{ color: '#64748b' }}>
              Get full access to all features at no cost. Build unlimited AI agents,
              send unlimited messages, and grow your business.
            </p>
          </div>
        </section>

        {/* Single Free Card */}
        <section className="px-6 pb-16">
          <div className="max-w-lg mx-auto">
            <div className="relative p-8 rounded-2xl"
                 style={{
                   background: '#ffffff',
                   border: '2px solid #22c55e',
                   boxShadow: '0 8px 24px rgba(34, 197, 94, 0.15)'
                 }}>
              {/* Badge */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-medium text-white"
                   style={{ background: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)' }}>
                Early Access
              </div>

              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
                     style={{ background: 'rgba(34, 197, 94, 0.1)' }}>
                  <Sparkles className="w-8 h-8" style={{ color: '#22c55e' }} />
                </div>
                <h3 className="text-2xl font-bold mb-2" style={{ color: '#1e293b' }}>All Features Included</h3>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-4xl font-bold" style={{ color: '#22c55e' }}>Free</span>
                </div>
                <p className="text-sm mt-2" style={{ color: '#64748b' }}>No credit card required</p>
              </div>

              <ul className="space-y-3 mb-8">
                {features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm" style={{ color: '#64748b' }}>
                    <Check className="w-5 h-5 shrink-0" style={{ color: '#22c55e' }} />
                    {feature}
                  </li>
                ))}
              </ul>

              <Link to="/" className="block w-full py-3 rounded-xl text-center font-medium text-white transition-all hover:scale-[1.02]"
                    style={{
                      background: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)',
                      boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)'
                    }}>
                Get Started Free
              </Link>

              {/* Trust indicators */}
              <div className="mt-6 pt-6 flex items-center justify-center gap-6" style={{ borderTop: '1px solid #e2e8f0' }}>
                <div className="flex items-center gap-2 text-xs" style={{ color: '#94a3b8' }}>
                  <Zap className="w-4 h-4" style={{ color: '#f59e0b' }} />
                  <span>10 min setup</span>
                </div>
                <div className="flex items-center gap-2 text-xs" style={{ color: '#94a3b8' }}>
                  <Clock className="w-4 h-4" style={{ color: '#6366f1' }} />
                  <span>24/7 availability</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="px-6 py-16" style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl lg:text-3xl font-bold text-center mb-12" style={{ color: '#1e293b' }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="p-5 rounded-xl"
                     style={{
                       background: '#ffffff',
                       border: '1px solid #e2e8f0'
                     }}>
                  <h4 className="font-semibold mb-2" style={{ color: '#1e293b' }}>{faq.question}</h4>
                  <p className="text-sm" style={{ color: '#64748b' }}>{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-6 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl lg:text-3xl font-bold mb-4" style={{ color: '#1e293b' }}>
              Ready to automate your customer support?
            </h2>
            <p className="mb-8" style={{ color: '#64748b' }}>
              Join businesses already using Botibonga to handle customer inquiries - completely free.
            </p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-medium transition-all hover:scale-105"
                  style={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
                  }}>
              Start Building Free
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="px-6 py-8" style={{ borderTop: '1px solid #e2e8f0' }}>
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm" style={{ color: '#94a3b8' }}>
              © {new Date().getFullYear()} Botibonga. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/terms" className="text-sm transition-colors" style={{ color: '#94a3b8' }}
                    onMouseEnter={(e) => e.target.style.color = '#6366f1'}
                    onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
                Terms
              </Link>
              <Link to="/privacy" className="text-sm transition-colors" style={{ color: '#94a3b8' }}
                    onMouseEnter={(e) => e.target.style.color = '#6366f1'}
                    onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
                Privacy
              </Link>
              <Link to="/contact" className="text-sm transition-colors" style={{ color: '#94a3b8' }}
                    onMouseEnter={(e) => e.target.style.color = '#6366f1'}
                    onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
                Contact
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
